/**
 * CL-PROPERTY-OS-002S — Portal email verification rollout policy (client mirror).
 *
 * Keep PORTAL_EMAIL_VERIFICATION_ROLLOUT_CUTOFF_ISO in sync with functions/main copy.
 *
 * Read-only Auth audit (2026-09-02): newest established account creationTime is
 * 2026-07-25T22:21:21.000Z; DEV TWO recreated account creationTime is
 * 2026-09-03T02:38:31.000Z. Cutoff is one second before DEV TWO.
 */

export const PORTAL_EMAIL_VERIFICATION_ROLLOUT_CUTOFF_ISO = "2026-09-03T02:38:30.000Z";

/** Sanitized audit anchor — newest established Auth account (read-only audit 2026-09-02). */
export const PORTAL_EMAIL_VERIFICATION_NEWEST_ESTABLISHED_CREATION_TIME_ISO =
  "2026-07-25T22:21:21.000Z";

/** Sanitized audit anchor — DEV TWO recreated Auth account (read-only audit 2026-09-02). */
export const PORTAL_EMAIL_VERIFICATION_DEV_TWO_CREATION_TIME_ISO = "2026-09-03T02:38:31.000Z";

export const PORTAL_EMAIL_VERIFICATION_ROLLOUT_CUTOFF_MS = Date.parse(
  PORTAL_EMAIL_VERIFICATION_ROLLOUT_CUTOFF_ISO
);

export const PORTAL_EMAIL_VERIFICATION_POLICY = Object.freeze({
  VERIFIED: "verified",
  GRANDFATHERED: "grandfathered",
  REQUIRES_VERIFICATION: "requires_verification",
  CURRENT_SESSION_SIGNUP: "current_session_signup",
  METADATA_UNAVAILABLE: "metadata_unavailable",
});

export const PORTAL_EMAIL_VERIFICATION_METADATA_ERROR_MESSAGE =
  "We could not confirm when this portal account was created. Sign out, then sign in again. If this continues, contact the office for help.";

function safeStr(value) {
  return String(value ?? "").trim();
}

export function parseAuthMetadataCreationTimeMillis(metadata = {}) {
  const raw = safeStr(metadata?.creationTime);
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function classifyPortalEmailVerificationRollout({
  emailVerified = false,
  metadata = {},
  isCurrentSignupSession = false,
} = {}) {
  if (emailVerified === true) {
    return {
      policy: PORTAL_EMAIL_VERIFICATION_POLICY.VERIFIED,
      requiresVerification: false,
      allowPortalAccess: true,
      blockLifecycleWrites: false,
      showVerificationPanel: false,
      autoSendVerificationEmail: false,
    };
  }

  if (isCurrentSignupSession) {
    return {
      policy: PORTAL_EMAIL_VERIFICATION_POLICY.CURRENT_SESSION_SIGNUP,
      requiresVerification: true,
      allowPortalAccess: false,
      blockLifecycleWrites: true,
      showVerificationPanel: true,
      autoSendVerificationEmail: true,
    };
  }

  const creationTimeMillis = parseAuthMetadataCreationTimeMillis(metadata);
  if (creationTimeMillis == null) {
    return {
      policy: PORTAL_EMAIL_VERIFICATION_POLICY.METADATA_UNAVAILABLE,
      requiresVerification: false,
      allowPortalAccess: false,
      blockLifecycleWrites: true,
      showVerificationPanel: false,
      autoSendVerificationEmail: false,
      clientErrorMessage: PORTAL_EMAIL_VERIFICATION_METADATA_ERROR_MESSAGE,
    };
  }

  if (creationTimeMillis < PORTAL_EMAIL_VERIFICATION_ROLLOUT_CUTOFF_MS) {
    return {
      policy: PORTAL_EMAIL_VERIFICATION_POLICY.GRANDFATHERED,
      requiresVerification: false,
      allowPortalAccess: true,
      blockLifecycleWrites: false,
      showVerificationPanel: false,
      autoSendVerificationEmail: false,
    };
  }

  return {
    policy: PORTAL_EMAIL_VERIFICATION_POLICY.REQUIRES_VERIFICATION,
    requiresVerification: true,
    allowPortalAccess: false,
    blockLifecycleWrites: true,
    showVerificationPanel: true,
    autoSendVerificationEmail: false,
  };
}

export function classifyPortalEmailVerificationRolloutFromUser(
  user = null,
  { isCurrentSignupSession = false } = {}
) {
  if (!user || user.isAnonymous) {
    return classifyPortalEmailVerificationRollout({
      emailVerified: false,
      metadata: {},
      isCurrentSignupSession: false,
    });
  }
  return classifyPortalEmailVerificationRollout({
    emailVerified: user.emailVerified === true,
    metadata: user.metadata || {},
    isCurrentSignupSession,
  });
}

export function portalEmailVerificationSatisfiedFromUser(user = null, options = {}) {
  if (!user || user.isAnonymous) return false;
  const classification = classifyPortalEmailVerificationRolloutFromUser(user, options);
  return classification.allowPortalAccess === true && classification.blockLifecycleWrites !== true;
}

export function shouldShowPortalEmailVerificationPanel({
  user = null,
  lifecyclePhase = null,
  isCurrentSignupSession = false,
  verificationLifecyclePhases = null,
} = {}) {
  const classification = classifyPortalEmailVerificationRolloutFromUser(user, { isCurrentSignupSession });
  if (!classification.showVerificationPanel) return false;
  if (!user || user.isAnonymous || user.emailVerified === true) return false;
  if (Array.isArray(verificationLifecyclePhases) && verificationLifecyclePhases.length) {
    return verificationLifecyclePhases.includes(lifecyclePhase);
  }
  return true;
}
